<script setup lang="ts">
import { v4 as uuidv4 } from "uuid";
import { ref, computed } from "vue";
import InputLabel from "./InputLabel.vue";
import InfoIcon from "@/features/Tooltip/InfoIcon.vue";

const props = withDefaults(
  defineProps<{
    modelValue: string | number | null;
    label?: string;
    placeholder?: string;
    disabled?: boolean;
    errorMsg?: string;
    id?: string;
    tooltip?: string;
    tooltipTitle?: string;
    required?: boolean;
    type?: "text" | "email" | "password" | "tel" | "url" | "search" | "number";
    /** Caps the length and shows a live counter, e.g. 12 / 55. */
    maxlength?: number;
    /** Drops leading whitespace as it is typed, so a pasted value cannot start with a space. */
    removeLeadingWhitespace?: boolean;
    /** Strips digits and punctuation, for fields that hold a person's name or initials. */
    textOnly?: boolean;
    /**
     * Keeps the error message's 20px band under the control whether or not there is a message in it,
     * so a field that starts erroring does not displace itself or anything aligned to it. Opt-in: on
     * by default it would put 20px of air under every field in the app. See the band in the template.
     */
    reserveErrorSpace?: boolean;
  }>(),
  {
    type: "text", // default value
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

const localId = ref(uuidv4());
const inputId = computed(() => props.id ?? `input-${localId.value}`);

/**
 * Same three rules, in the same order, as the older TextInput this component replaces, so a field can move
 * across without changing what a user is allowed to type.
 */
const sanitise = (value: string): string => {
  let next = value;
  if (props.textOnly) next = next.replace(/[\d\W]/g, "");
  if (props.maxlength !== undefined && next.length > props.maxlength) next = next.slice(0, props.maxlength);
  if (props.removeLeadingWhitespace) next = next.trimStart();
  return next;
};

const onInput = (e: Event) => {
  const target = e.target as HTMLInputElement;
  const next = sanitise(target.value);

  /**
   * Write the cleaned value straight back to the element. The input is controlled by `modelValue`, so when
   * sanitising leaves the value unchanged from the parent's point of view — typing a digit into a textOnly
   * field, say — no prop changes, nothing re-renders, and the rejected character would sit there visibly.
   */
  if (next !== target.value) target.value = next;

  emit("update:modelValue", next);
};
</script>

<template>
  <div class="flex w-full flex-col">
    <!-- h-6 for the same reason as InputLabel's own row: this wrapper holds the label beside an InfoIcon
         whose hit area is 24px, so without a fixed height a field with a tooltip sits 8px lower than one
         without, and two of them in a row do not line up. Both must be pinned, because the icon lives
         inside the label in SelectInputWithKeyValue and beside it here. 24px is the icon's own box, and it
         leaves 4px under the label text so it is not flush against the control.

         Only rendered when there is something to put in it: unconditionally, a label-less field carried an
         empty 24px row above its input, which pushed the control 12px below every sibling in a centred row
         — the History page toolbar's search box against the filter / date / Export buttons beside it
         (RTLCA-5587). Nothing is lost for labelled fields, which is every other usage. -->
    <div
      v-if="label || tooltip"
      class="flex h-6 items-center">
      <InputLabel
        :id="inputId"
        :label="label"
        :required="required" />
      <InfoIcon
        v-if="tooltip"
        :tooltip="tooltip"
        :tooltip-title="tooltipTitle" />
    </div>
    <div class="relative w-full">
      <input
        :id="inputId"
        :type="type"
        :maxlength="maxlength"
        class="focus-input rounded-8 z-10 h-9 w-full border py-2 pr-9 pl-3 text-sm leading-none"
        :placeholder="placeholder"
        :disabled="disabled"
        :value="modelValue ?? ''"
        :aria-required="required ? true : undefined"
        :class="[
          errorMsg ? 'border-app-red-500' : 'border-slate-300',
          disabled ? 'text-type-muted cursor-not-allowed bg-slate-200' : 'bg-slate-0',
          maxlength !== undefined ? 'pr-[76px]' : '',
        ]"
        @input="onInput" />
      <!-- aria-hidden: the native maxlength already tells assistive tech the limit, and a live count read
           on every keystroke is noise. -->
      <span
        v-if="maxlength !== undefined"
        class="text-type-muted absolute top-1/2 right-3 -translate-y-1/2 text-xs"
        aria-hidden="true">
        {{ String(modelValue ?? "").length }} / {{ maxlength }}
      </span>
    </div>

    <!-- Two spellings of the same message. In the default one the band exists only while there is
         something to say, so a field grows when it starts erroring — fine for a stacked form, wrong for
         a row whose other columns align to this field's bottom edge (the transport type row, RTLCA-5771:
         "Enter a name" under Name pushed Distance / Emission / Total emission a message's height out of
         line). `reserveErrorSpace` holds the band open at its erroring height — mt-1 plus one 16px line —
         so the control never moves. `min-h-4`, not `h-4`: a message that wraps to two lines still shifts
         the row, but is readable rather than clipped. `text-xs` on the BAND as well as the message, because
         an empty band's height is its own strut — inheriting the 16px body font, it stood 24px tall and
         reserved 8px more than the message it was standing in for.

         The message stays its own `v-if` INSIDE the band rather than a class on it, because `role="alert"`
         announces reliably when the element carrying it is inserted with its text, not when text appears
         in an element that was already there. -->
    <div
      v-if="reserveErrorSpace"
      class="mt-1 min-h-4 text-xs">
      <span
        v-if="errorMsg"
        role="alert"
        class="text-app-red-700 text-xs">
        {{ errorMsg }}
      </span>
    </div>
    <span
      v-else-if="errorMsg"
      role="alert"
      class="text-app-red-700 mt-1 text-xs">
      {{ errorMsg }}
    </span>
  </div>
</template>
